import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.

export const diabetesJames: Scenario = {
  slug: 'diabetes-james',
  series: 'Metabolic Health',
  title: "He looked fit. His blood sugar told a different story.",
  socialHook:
    "James, 47: healthy weight, regular runner. His fasting glucose kept rising. The clue wasn't his weight — it was late insulin timing, insulin resistance, and hidden fat his BMI could not see.",
  meta: {
    description:
      "A fictional case study: James has a healthy BMI and runs regularly, yet his glucose keeps rising. His story points away from weight loss and toward beta-cell timing, insulin sensitivity, and visceral fat hidden behind a normal BMI.",
  },

  persona: {
    name: 'James',
    age: 47,
    sex: 'Male',
    ancestry: 'White Irish',
    occupation: 'Financial analyst',
    chiefConcern:
      "His fasting glucose has crept up for three years and is now 110 mg/dL — pre-diabetic range. He has a BMI of 23.4 and runs three times a week. His family doctor keeps saying lose weight and exercise more. James is stuck because he already does.",
    familyHistory:
      'Father: type 2 diabetes diagnosed at 52. Brother: type 2 diabetes diagnosed at 49. Both were slim when diagnosed.',
  },

  clinicalPicture: {
    symptoms: [
      'No symptoms — he feels well',
      'Fasting glucose creeping up over three consecutive annual bloods',
      'Waist circumference 89 cm despite BMI 23.4',
      'Runs 3x/week — considers himself active and healthy',
    ],
    labs: [
      {
        name: 'Fasting Glucose',
        value: '110 mg/dL',
        refRange: '<100 mg/dL',
        flag: 'high',
      },
      {
        name: 'HOMA-IR (insulin resistance index)',
        value: '3.8',
        refRange: '<2.5',
        flag: 'high',
      },
    ],
    medications: ['No prescription medications'],
    supplements: ['No regular supplements'],
    lifestyle: [
      'Runs 3x/week — approximately 25–30 km/week',
      'No resistance or strength training',
      'Diet: self-described "balanced" — not tracked formally',
      'Non-smoker, alcohol 8–10 drinks/week',
      'Sleep: 7 hours, generally uninterrupted',
    ],
  },

  genetics: [
    {
      gene: 'TCF7L2',
      variant: 'rs7903146',
      rsid: 'rs7903146',
      genotype: 'T allele carrier',
      plainMeaning:
        "His pancreas is slower to get insulin ready after food. This is not about willpower; TCF7L2 helps explain why his insulin response starts late.",
      technicalDetail:
        "TCF7L2 is the strongest common genetic variant for type 2 diabetes risk in European populations. It governs how beta cells respond to incretins — the gut hormones that signal the pancreas to prepare insulin before glucose from a meal arrives. The T allele impairs that incretin signaling, making the pancreas reactive rather than anticipatory: insulin fires later and at a lower level per unit of glucose. This is a structural baseline, not a lifestyle consequence.",
    },
    {
      gene: 'PPARG',
      variant: 'rs1801282 (Pro12Ala)',
      rsid: 'rs1801282',
      genotype: 'Pro allele (common)',
      plainMeaning:
        "The insulin he does make has to work harder than average. PPARG helps explain why his muscle and fat cells do not absorb glucose as easily.",
      technicalDetail:
        "PPARG is the master transcription factor for fat cell differentiation and insulin sensitivity in fat and muscle tissue. The Pro allele is associated with reduced insulin sensitivity: his cells require more insulin per unit of glucose uptake than average. Combined with TCF7L2's impaired beta-cell output, James faces a double bind — less insulin released, and that insulin having less effect at the tissue level.",
    },
    {
      gene: 'FTO',
      variant: 'rs9939609',
      rsid: 'rs9939609',
      genotype: 'A allele carrier',
      plainMeaning:
        "Even at a healthy weight, his body may store more fat around the organs. FTO helps explain the waist measurement his BMI missed.",
      technicalDetail:
        "FTO is the strongest common obesity and body-composition variant. The A allele is associated with preferential visceral fat deposition — fat stored around abdominal organs rather than subcutaneously. His waist circumference at a lean BMI fits this pattern precisely. Visceral fat is metabolically active in a damaging way: it releases inflammatory cytokines and free fatty acids directly into the portal circulation feeding the liver, driving insulin resistance independently of overall body weight.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: 'The usual advice did not fit the evidence',
      body: "James is 47, lean, and runs three times a week. On paper, he looks like the person who should not be drifting toward diabetes. But his fasting glucose has climbed at every annual blood test and is now in the pre-diabetic range. His father developed type 2 diabetes at 52. His brother at 49. Both were slim. When James hears “lose weight and exercise more,” it feels like a door closing, because the obvious explanation does not match the case.",
    },
    {
      type: 'narrative',
      heading: "The missing clue was not weight",
      body: "Pre-diabetes is often treated like a weight problem. For James, it is a timing and sensitivity problem. His HbA1c looks reassuring, but his insulin resistance index says his body is working too hard to keep glucose steady. His waist measurement adds the clue BMI missed: for a man his size, more fat is sitting around the middle. BMI measures total size. Waist circumference hints at where fat is stored. Those are different questions, and they point to different fixes.",
    },
    {
      type: 'reveal',
      heading: 'His pancreas was answering late',
      body: "The first reveal is timing: James's pancreas is slower to prepare insulin before a meal. His TCF7L2 variant weakens the early gut-to-pancreas signal that normally gets insulin ready as food arrives. That means glucose can rise higher after meals, not because he ate “badly,” but because the early-warning system runs slow. Lifestyle can compensate for this. It did not cause it.",
    },
    {
      type: 'reveal',
      heading: "His insulin had less effect, and his fat was hiding",
      body: "The second reveal is sensitivity: the insulin James makes is not going as far as it should. His PPARG variant helps explain why his muscles and fat cells take up glucose less efficiently, and his elevated insulin resistance index confirms the pattern. The third reveal is location: his FTO variant is linked with more fat stored around organs such as the liver and pancreas. That internal fat can disrupt metabolism even when the scale looks fine. His waist measurement was the hint. His genetic profile explains why it mattered.",
    },
    {
      type: 'action',
      heading: "Five moves that match the real problem",
      items: [
        "Measure the right target: waist circumference and waist-to-height ratio, not just weight. His goal is less visceral fat around the organs, not becoming thinner overall.",
        "Add resistance training three times a week alongside running. More muscle gives glucose somewhere to go after meals and directly helps the PPARG-related insulin resistance pattern.",
        "Pair carbohydrates with protein and fat. Slower glucose entry gives his slower TCF7L2-linked insulin response more time to catch up.",
        "Ask his family doctor about metformin in high-risk pre-diabetes. James has impaired fasting glucose, two first-degree relatives with type 2 diabetes, and an elevated insulin resistance index.",
        "Recheck fasting glucose and insulin in six months, not twelve. With this pattern, waiting a full year could miss the chance to adjust while the window is still open.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'riskdial' as const,
      heading: "What is really driving James's glucose",
      caption:
        "The mystery is not weight. It is beta-cell timing, insulin sensitivity, and hidden visceral fat. These levers target those mechanisms directly.",
      baselineLabel: 'Current trajectory — genetic + lifestyle baseline',
      baseline: 74,
      levers: [
        {
          label: 'Resistance training 3x/week',
          delta: -14,
          description:
            'Builds muscle that can absorb glucose after meals. Directly addresses PPARG-related insulin resistance.',
        },
        {
          label: 'Carb timing (with protein/fat)',
          delta: -10,
          description:
            "Slows glucose entry and gives the TCF7L2-linked late insulin response more time to catch up.",
        },
        {
          label: 'Waist circumference reduction (targeted)',
          delta: -12,
          description:
            "Targets visceral fat around the liver and pancreas — the FTO-linked pattern that BMI can't see.",
        },
        {
          label: 'Early metformin (with family doctor)',
          delta: -18,
          description:
            "A strong option to discuss for high-risk pre-diabetes. Improves liver insulin sensitivity and reduces glucose production.",
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'diabetes-james',
    label: 'Explore your metabolic genetics in Coach',
  },
}
